import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../../utils/auth';
import { ArrowUpRight } from 'lucide-react';

const AppliedApplications = () => {
  const { user } = useUser();
  const [appliedPrograms, setAppliedPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Status mapping
  const statusLabels = {
    0: 'Pending',
    1: 'Sent to University',
    2: 'Offer Received',
    3: 'Documents Pending',
    4: 'All Done'
  };

  useEffect(() => {
    const fetchAppliedPrograms = async () => {
      if (!user?.id) return;
      
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/applied-programs/${user.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log(response.data);
        if (response.data.success) {
          setAppliedPrograms(response.data.programs);
        } else {
          setError('Failed to fetch applied programs');
        }
      } catch (error) {
        console.error('Error fetching applied programs:', error);
        setError('An error occurred while fetching your applied programs');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAppliedPrograms();
  }, [user]);

  // Map status to suitable styling
  const getStatusStyle = (statusValue) => {
    const styles = {
      0: "bg-amber-100 text-amber-800",    // Pending
      1: "bg-blue-100 text-blue-800",      // Sent to University
      2: "bg-emerald-100 text-emerald-800", // Offer Received
      3: "bg-orange-100 text-orange-800",  // Documents Pending
      4: "bg-indigo-100 text-indigo-800"   // All Done
    };
    return styles[statusValue] || "bg-gray-100 text-gray-800";
  };
  
  // Helper function to get status label
  const getStatusLabel = (statusValue) => {
    return statusLabels[statusValue] || 'Unknown';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (appliedPrograms.length === 0) {
    return (
      <div className="text-center py-8">
        <h3 className="text-xl font-semibold mb-2">No Applications Yet</h3>
        <p className="text-gray-600 mb-4">You haven't applied to any programs yet.</p>
        <a 
          href="/programs" 
          className="inline-block px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          Browse Programs
        </a>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold mb-6">My Applications</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {appliedPrograms.map((program) => (
          <div 
            key={program.programId} 
            className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-lg">{program.universityName.toUpperCase()}</h3>
                <p className="text-gray-700 uppercase">{program.programName}</p>
                
                <div className="mt-2">
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full mr-2">
                    Applied
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(program.appliedDate).toLocaleDateString()}
                  </span>
                </div>
                
                {program.status !== undefined && (
                  <div className="mt-2">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusStyle(program.status)}`}>
                      Status: {getStatusLabel(program.status)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                <a 
                  href="https://calendly.com/applicationuni" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline"
                >
                  Schedule Counseling Call
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppliedApplications; 